import React, { useEffect, useState } from "react";
import { SelectedAddress } from "../../../types/selectedAddress";
import CreateUserListing from "./CreateUserListing";
import ConfirmUserListing from "./ConfirmUserListing";
import { UserListing } from "../../../types/userListing";
import MediaAdder from "./MediaAdder";
import { useIsMobile } from "../../../hooks/useIsMobile";
import DescriptionAdder from "./DescriptionAdder";
import useAppSelector from "../../../hooks/useAppSelector";

interface CreateListingsProps {
    mlsApproved: boolean;
    mlsWelcome: boolean;
}

const CreateListings: React.FC<CreateListingsProps> = () => {
    // Read directly from Redux to ensure impersonation state is current
    const mlsApproved = useAppSelector((state) => state.auth.mlsApproved);
    const mlsWelcome = useAppSelector((state) => state.auth.user?.mlsWelcome || false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [selectedAddress, setSelectedAddress] =
        useState<SelectedAddress | null>(null);
    const [mlsId, setMlsId] = useState<string>("");
    const [saleType, setSaleType] = useState<"sale" | "rent">("sale");
    const [listing, setListing] = useState<UserListing | null>(null);
    const [sharedListing, setSharedListing] = useState<boolean>(false);
    // Whether to persist this listing's MLS agent identity onto the user (and
    // enable auto-ingest) — decided in the add-listing agent-confirmation step,
    // consumed by the create POST (here and in DescriptionAdder).
    const [saveMlsAgentDetails, setSaveMlsAgentDetails] =
        useState<boolean>(false);

    const isMobile = useIsMobile(700);

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <CreateUserListing
                        setCurrentStep={setCurrentStep}
                        selectedAddress={selectedAddress}
                        setSelectedAddress={setSelectedAddress}
                        mlsApproved={mlsApproved}
                        mlsId={mlsId}
                        setMlsId={setMlsId}
                        mlsWelcome={mlsWelcome}
                        setListing={setListing}
                        saleType={saleType}
                        setSaleType={setSaleType}
                        sharedListing={sharedListing}
                        setSharedListing={setSharedListing}
                    />
                );
            case 2:
                return (
                    <ConfirmUserListing
                        setCurrentStep={setCurrentStep}
                        selectedAddress={selectedAddress}
                        mlsApproved={mlsApproved}
                        mlsId={mlsId}
                        listing={listing}
                        setCreatedListing={setListing}
                        saleType={saleType}
                        setSaleType={setSaleType}
                        listingType={listing?.listingType}
                        sharedListing={sharedListing}
                        setSharedListing={setSharedListing}
                        setSaveMlsAgentDetails={setSaveMlsAgentDetails}
                    />
                );
            case 3:
                return (
                    <MediaAdder
                        isMobile={isMobile}
                        mlsApproved={mlsApproved}
                        propertyData={listing}
                        setPropertyData={setListing}
                        setCurrentStep={setCurrentStep}
                    />
                );
            case 4:
                return (
                    <DescriptionAdder
                        isMobile={isMobile}
                        listing={listing}
                        setListing={setListing}
                        setCurrentStep={setCurrentStep}
                        mlsId={mlsId}
                        saleType={saleType}
                        sharedListing={sharedListing}
                        saveMlsAgentDetails={saveMlsAgentDetails}
                    />
                );
            default:
                return (
                    <CreateUserListing
                        setCurrentStep={setCurrentStep}
                        selectedAddress={selectedAddress}
                        setSelectedAddress={setSelectedAddress}
                        mlsApproved={mlsApproved}
                        mlsId={mlsId}
                        setMlsId={setMlsId}
                        mlsWelcome={mlsWelcome}
                        setListing={setListing}
                        saleType={saleType}
                        setSaleType={setSaleType}
                        sharedListing={sharedListing}
                        setSharedListing={setSharedListing}
                    />
                );
        }
    };

    useEffect(() => {}, [selectedAddress]);

    return <div className="relative w-full h-full">{renderStep()}</div>;
};

export default CreateListings;
